import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { productionService, fileService } from '../services/apiService';
import api from '../services/api';
import { getFullImageUrl } from '../utils/imageUtils';
import { getDeadlineFlag, DEADLINE_STYLES } from '../utils/deadlineFlag';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import {
  Users,
  ArrowRight,
  CheckCircle,
  XCircle,
  Package,
  Gem,
  CircleDot,
  Upload,
  X,
  Diamond,
  Camera,
  Images as ImagesIcon
} from 'lucide-react';

const STAGE_CONFIG = {
  new: { label: 'New', color: 'bg-gray-500', icon: '🆕' },
  casting: { label: 'Casting', color: 'bg-orange-500', icon: '🔥' },
  stone_setting: { label: 'Stone Setting', color: 'bg-blue-500', icon: '💎' },
  polishing: { label: 'Polishing', color: 'bg-yellow-500', icon: '✨' },
  rhodium: { label: 'Rhodium', color: 'bg-purple-500', icon: '🪙' },
  final_qc: { label: 'Final QC', color: 'bg-indigo-500', icon: '🔍' },
  completed: { label: 'Completed', color: 'bg-green-500', icon: '✅' },
};

const ProductionPanel = ({ order, onOrderUpdate, isMobile = false }) => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [workers, setWorkers] = useState([]);
  const [stages, setStages] = useState([]);
  const [inventoryUsers, setInventoryUsers] = useState([]);
  const [brokers, setBrokers] = useState([]);  // NEW: Broker list
  const [materialRequests, setMaterialRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [workerDialogOpen, setWorkerDialogOpen] = useState(false);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [qcDialogOpen, setQcDialogOpen] = useState(false);
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [jobCardDialogOpen, setJobCardDialogOpen] = useState(false);
  const [confirmIssuanceDialogOpen, setConfirmIssuanceDialogOpen] = useState(false);
  const [brokerDialogOpen, setBrokerDialogOpen] = useState(false);  // NEW: Broker assignment dialog
  const [selectedRequestForConfirm, setSelectedRequestForConfirm] = useState(null);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [completionImage, setCompletionImage] = useState('');
  const [completionGrossWeight, setCompletionGrossWeight] = useState('');
  const [completionNetWeight, setCompletionNetWeight] = useState('');
  const [completionStoneCount, setCompletionStoneCount] = useState('');
  const [completionPurity, setCompletionPurity] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [uploadingCompletion, setUploadingCompletion] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [acceptForm, setAcceptForm] = useState({
    committed_delivery_date: '', design_freeze: false,
    advance_received: false, advance_amount: '',
  });
  const [feasibilityLoading, setFeasibilityLoading] = useState(false);
  const [feasibilityResponseDept, setFeasibilityResponseDept] = useState('');
  const [feasibilityResponseStatus, setFeasibilityResponseStatus] = useState('');
  const [feasibilityArrangeBy, setFeasibilityArrangeBy] = useState('');

  // Form states
  const [selectedWorker, setSelectedWorker] = useState('');
  const [workerNotes, setWorkerNotes] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [stageNotes, setStageNotes] = useState('');
  const [qcNotes, setQcNotes] = useState('');
  const [selectedBroker, setSelectedBroker] = useState('');  // NEW: Broker selection
  const [brokerNotes, setBrokerNotes] = useState('');  // NEW: Broker assignment notes

  // Material Request Form
  const [materialType, setMaterialType] = useState('gold');
  const [assignedInventory, setAssignedInventory] = useState('');
  const [requestWorker, setRequestWorker] = useState(''); // Worker for this request
  const [materialComments, setMaterialComments] = useState('');
  const [materialImage, setMaterialImage] = useState(null);
  const [materialImageUrl, setMaterialImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  // Gold fields
  const [metalPurity, setMetalPurity] = useState('22K');
  const [weightGrams, setWeightGrams] = useState('');
  // Diamond/Stone fields
  const [weightCarats, setWeightCarats] = useState('');
  const [materialCount, setMaterialCount] = useState('');
  // Other material - ENHANCED
  const [otherMaterialName, setOtherMaterialName] = useState('');
  const [otherMaterialCategory, setOtherMaterialCategory] = useState('gold_ornaments');
  const [otherMaterialPurity, setOtherMaterialPurity] = useState('');
  const [quantityPieces, setQuantityPieces] = useState('');
  const [quantityWeight, setQuantityWeight] = useState('');
  // Diamond priority
  const [diamondPriority, setDiamondPriority] = useState('normal');

  // Job card
  const [jobCardNumber, setJobCardNumber] = useState('');

  // Confirm Material Issuance Form
  const [issuanceType, setIssuanceType] = useState('full'); // 'full' or 'partial'
  const [weightIssuingNow, setWeightIssuingNow] = useState('');
  const [voucherNumber, setVoucherNumber] = useState('');
  const [needsParcel, setNeedsParcel] = useState(false);
  const [actualWeightIssued, setActualWeightIssued] = useState('');
  const [parcelRate, setParcelRate] = useState('');
  const [insuranceValue, setInsuranceValue] = useState(0);
  // Diamond-specific issuance fields - now as array for multiple categories
  const [diamondEntries, setDiamondEntries] = useState([{
    category: '',
    cts: '',
    grams: '',
    pieces: '',
    description: ''
  }]);
  const [allRequirementsCleared, setAllRequirementsCleared] = useState(false);

  const role = currentUser?.role || 'member';

  useEffect(() => {
    loadData();
  }, [order?.id]);

  // Calculate insurance value when actual weight or rate changes
  useEffect(() => {
    if (needsParcel && actualWeightIssued && parcelRate) {
      const weight = parseFloat(actualWeightIssued);
      const rate = parseFloat(parcelRate);
      if (!isNaN(weight) && !isNaN(rate)) {
        setInsuranceValue(weight * rate);
      } else {
        setInsuranceValue(0);
      }
    } else {
      setInsuranceValue(0);
    }
  }, [needsParcel, actualWeightIssued, parcelRate]);

  // Auto-fill actual weight when "Needs Parcel" is checked
  useEffect(() => {
    if (needsParcel && !actualWeightIssued) {
      // For diamond/stone requests, calculate total from entries
      const materialType = selectedRequestForConfirm?.material_type?.toLowerCase();
      if (materialType === 'diamond' || materialType === 'stone' || materialType === 'colour_stone') {
        const totalCts = diamondEntries.reduce((sum, e) => sum + (parseFloat(e.cts) || 0), 0);
        if (totalCts > 0) {
          setActualWeightIssued(totalCts.toFixed(3));
        } else if (weightIssuingNow) {
          setActualWeightIssued(weightIssuingNow);
        }
      } else {
        // For non-diamond/stone, use weight issuing now
        if (weightIssuingNow) {
          setActualWeightIssued(weightIssuingNow);
        }
      }
    }
  }, [needsParcel, weightIssuingNow, actualWeightIssued, diamondEntries, selectedRequestForConfirm]);

  // Auto-calculate "Weight Issuing Now" from diamond/stone entries total
  useEffect(() => {
    const materialType = selectedRequestForConfirm?.material_type?.toLowerCase();
    if (materialType === 'diamond' || materialType === 'stone' || materialType === 'colour_stone') {
      const totalCts = diamondEntries.reduce((sum, e) => sum + (parseFloat(e.cts) || 0), 0);
      if (totalCts > 0) {
        setWeightIssuingNow(totalCts.toFixed(3));
      }
    }
  }, [diamondEntries, selectedRequestForConfirm]);

  // Helper functions for diamond entries
  const addDiamondEntry = () => {
    setDiamondEntries([...diamondEntries, {
      category: '',
      cts: '',
      grams: '',
      pieces: '',
      description: ''
    }]);
  };

  const removeDiamondEntry = (index) => {
    setDiamondEntries(diamondEntries.filter((_, i) => i !== index));
  };

  const updateDiamondEntry = (index, field, value) => {
    const updated = [...diamondEntries];
    updated[index][field] = value;

    // Auto-calculate grams if cts changed
    if (field === 'cts') {
      const cts = parseFloat(value);
      if (!isNaN(cts) && cts > 0) {
        updated[index].grams = (cts * 0.200).toFixed(3);
      } else {
        updated[index].grams = '';
      }
    }

    setDiamondEntries(updated);
  };

  const loadData = async () => {
    try {
      const [workersRes, stagesRes, inventoryRes, brokersRes] = await Promise.all([
        productionService.getWorkers().catch(() => []),
        productionService.getStages().catch(() => ({ stages: [] })),
        api.get('/users/by-role/inventory_dept').catch(() => ({ data: [] })),
        api.get('/users/by-role/broker').catch(() => ({ data: [] })),
      ]);
      setWorkers(workersRes);
      setStages(stagesRes.stages || []);
      setInventoryUsers(inventoryRes.data || []);
      setBrokers(brokersRes.data || []);

      // Set default worker to currently assigned worker
      if (order?.assigned_worker_id) {
        setRequestWorker(order.assigned_worker_id);
      }

      // Load material requests for this order
      if (order?.id) {
        const requestsRes = await api.get(`/orders/${order.id}/material-requests`).catch(() => ({ data: [] }));
        setMaterialRequests(requestsRes.data || []);
      }
    } catch (error) {
      console.error('Error loading production data:', error);
    }
  };

  const canRecommendWorker = ['admin', 'order_advisor'].includes(role);
  const canAssignWorker = ['admin', 'production_supervisor'].includes(role);
  const canChangeStage = ['admin', 'production_supervisor', 'worker', 'qc'].includes(role);
  const canQCApprove = ['admin', 'qc'].includes(role);
  const canRequestMaterial = ['admin', 'production_supervisor'].includes(role);
  const canConfirmMaterial = ['admin', 'inventory_dept'].includes(role);
  const canAssignBroker = ['admin', 'salesman'].includes(role);  // NEW: Broker assignment permission
  const canUploadCompletion = ['admin', 'inventory_dept', 'production_supervisor'].includes(role);
  const canVerifyCompletion = ['admin', 'salesman', 'order_advisor'].includes(role);
  const canAcceptOrder = ['admin', 'production_supervisor'].includes(role);

  const handleRecommendWorker = async () => {
    if (!selectedWorker) return;
    setLoading(true);
    try {
      await productionService.recommendWorker(order.id, selectedWorker, workerNotes);
      toast({ title: 'Worker recommended successfully' });
      setWorkerDialogOpen(false);
      setSelectedWorker('');
      setWorkerNotes('');
      onOrderUpdate?.();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to recommend worker', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };


  const handleAssignBroker = async () => {
    if (!selectedBroker) return;
    setLoading(true);
    try {
      await api.patch(`/orders/${order.id}/assign-broker`, {
        broker_id: selectedBroker,
        notes: brokerNotes
      });
      toast({ title: 'Broker assigned successfully' });
      setBrokerDialogOpen(false);
      setSelectedBroker('');
      setBrokerNotes('');
      onOrderUpdate?.();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to assign broker',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignWorker = async () => {
    if (!selectedWorker) return;
    setLoading(true);
    try {
      await productionService.assignWorker(order.id, selectedWorker, workerNotes);
      toast({ title: 'Worker assigned successfully' });
      setWorkerDialogOpen(false);
      setSelectedWorker('');
      setWorkerNotes('');
      onOrderUpdate?.();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to assign worker', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStage = async () => {
    if (!selectedStage) return;
    setLoading(true);
    try {
      await productionService.changeStage(order.id, selectedStage, stageNotes);
      toast({ title: 'Stage changed successfully' });
      setStageDialogOpen(false);
      setSelectedStage('');
      setStageNotes('');
      onOrderUpdate?.();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to change stage', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleQCApprove = async (approved) => {
    setLoading(true);
    try {
      await productionService.qcApprove(order.id, approved, qcNotes);
      toast({ title: approved ? 'QC Approved' : 'QC Rejected' });
      setQcDialogOpen(false);
      setQcNotes('');
      onOrderUpdate?.();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to submit QC', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleIssueMaterial = async () => {
    if (!materialType || !assignedInventory) {
      alert('Please select material type and inventory assistant');
      return;
    }

    if (!requestWorker) {
      toast({
        title: 'Error',
        description: 'Worker selection is mandatory. Please select which worker will receive these materials.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Upload image if selected
      let imageUrl = materialImageUrl;
      if (materialImage && !materialImageUrl) {
        const result = await fileService.uploadFile(materialImage);
        imageUrl = result.url;
      }

      // Create material request
      const requestData = {
        material_type: materialType,
        assigned_to: assignedInventory,
        worker_id: requestWorker,  // MANDATORY worker selection
        priority: materialType === 'diamond' ? diamondPriority : 'normal',
        comments: materialComments,
        image_url: imageUrl,
        metal_purity: materialType === 'gold' ? metalPurity : null,
        weight_grams: materialType === 'gold' && weightGrams ? parseFloat(weightGrams) : null,
        weight_carats: (materialType === 'diamond' || materialType === 'stone') && weightCarats ? parseFloat(weightCarats) : null,
        count: (materialType === 'diamond' || materialType === 'stone') && materialCount ? parseInt(materialCount) : null,
        // Other material - ENHANCED
        other_material_name: materialType === 'other' ? otherMaterialName : null,
        other_material_category: materialType === 'other' ? otherMaterialCategory : null,
        other_material_purity: materialType === 'other' ? otherMaterialPurity : null,
        quantity_pieces: materialType === 'other' && quantityPieces ? parseInt(quantityPieces) : null,
        quantity_weight: materialType === 'other' && quantityWeight ? parseFloat(quantityWeight) : null,
      };

      const response = await api.post(`/orders/${order.id}/material-requests`, requestData);

      alert(`✅ Material request created!\nRequest #: ${response.data.request_number}`);
      toast({ title: 'Material request created', description: response.data.request_number });

      // Reset form
      setMaterialDialogOpen(false);
      setMaterialType('gold');
      setAssignedInventory('');
      setRequestWorker(order?.assigned_worker_id || ''); // Reset to current assigned worker
      setMaterialComments('');
      setMaterialImage(null);
      setMaterialImageUrl('');
      setMetalPurity('22K');
      setWeightGrams('');
      setWeightCarats('');
      setMaterialCount('');
      setOtherMaterialName('');
      setOtherMaterialCategory('gold_ornaments');
      setOtherMaterialPurity('');
      setQuantityPieces('');
      setQuantityWeight('');
      setDiamondPriority('normal');
      onOrderUpdate?.();
    } catch (error) {
      console.error('Error creating material request:', error);
      alert('❌ Error: ' + (error.response?.data?.detail || 'Failed to create material request'));
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create material request', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignJobCard = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/orders/${order.id}/assign-job-card`, {
        job_card_number: jobCardNumber.trim() || undefined
      });

      alert(`✅ Job Card Assigned!\n${response.data.job_card_number}`);
      toast({ title: 'Job card assigned', description: response.data.job_card_number });
      setJobCardDialogOpen(false);
      setJobCardNumber('');
      onOrderUpdate?.();
    } catch (error) {
      alert('❌ Error: ' + (error.response?.data?.detail || 'Failed to assign job card'));
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to assign job card', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const currentStageConfig = STAGE_CONFIG[order?.current_stage] || STAGE_CONFIG.new;


  const handleConfirmMaterialIssuance = async () => {
    if (!voucherNumber.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter voucher/issue number',
        variant: 'destructive',
      });
      return;
    }

    if (!weightIssuingNow || parseFloat(weightIssuingNow) <= 0) {
      toast({
        title: 'Error',
        description: 'Please enter weight being issued',
        variant: 'destructive',
      });
      return;
    }

    if (needsParcel) {
      if (!actualWeightIssued || !parcelRate) {
        toast({
          title: 'Error',
          description: 'Please enter actual weight and rate for parcel',
          variant: 'destructive',
        });
        return;
      }
    }

    setLoading(true);
    try {
      // Prepare diamond entries array with only filled entries
      const validDiamondEntries = diamondEntries
        .filter(entry => entry.category || entry.cts || entry.pieces)
        .map(entry => ({
          category: entry.category || '',
          cts: entry.cts ? parseFloat(entry.cts) : 0,
          grams: entry.grams ? parseFloat(entry.grams) : 0,
          pieces: entry.pieces ? parseInt(entry.pieces) : 0,
          description: entry.description || ''
        }));

      const confirmData = {
        issuance_type: issuanceType,
        weight_issuing_now: parseFloat(weightIssuingNow),
        voucher_number: voucherNumber,
        needs_parcel: needsParcel,
        actual_weight_issued: needsParcel ? parseFloat(actualWeightIssued) : null,
        parcel_rate: needsParcel ? parseFloat(parcelRate) : null,
        insurance_value: needsParcel ? insuranceValue : null,
        // Send diamond entries array
        diamond_entries: validDiamondEntries,
        all_requirements_cleared: allRequirementsCleared,
      };

      const response = await api.patch(`/material-requests/${selectedRequestForConfirm.id}/confirm`, confirmData);

      const statusText = response.data.status === 'issued' ? 'fully issued' : 'partially issued';
      toast({
        title: `Material ${statusText}`,
        description: `Request ${selectedRequestForConfirm.request_number} confirmed`
      });

      // Reset form and close dialog
      setConfirmIssuanceDialogOpen(false);
      setSelectedRequestForConfirm(null);
      setIssuanceType('full');
      setWeightIssuingNow('');
      setVoucherNumber('');
      setNeedsParcel(false);
      setActualWeightIssued('');
      setParcelRate('');
      setInsuranceValue(0);
      // Reset diamond entries array
      setDiamondEntries([{
        category: '',
        cts: '',
        grams: '',
        pieces: '',
        description: ''
      }]);
      setAllRequirementsCleared(false);

      loadData();
      onOrderUpdate?.();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to confirm material issuance',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const openConfirmIssuanceDialog = (request) => {
    setSelectedRequestForConfirm(request);

    // Calculate remaining amount
    const requested = request.weight_grams || request.weight_carats || 0;
    const totalIssued = request.total_issued || 0;
    const remaining = requested - totalIssued;

    // Set default weight to remaining amount
    setWeightIssuingNow(remaining.toString());
    setIssuanceType('full');
    setVoucherNumber('');
    setNeedsParcel(false);
    setActualWeightIssued('');
    setParcelRate('');
    setInsuranceValue(0);
    // Reset diamond entries array
    setDiamondEntries([{
      category: '',
      cts: '',
      grams: '',
      pieces: '',
      description: ''
    }]);
    setAllRequirementsCleared(false);
    setConfirmIssuanceDialogOpen(true);
  };

  // Order acceptance handler
  const handleAcceptOrder = async () => {
    if (!acceptForm.committed_delivery_date) {
      toast({ title: 'Committed delivery date is required', variant: 'destructive' }); return;
    }
    setLoading(true);
    try {
      await api.post(`/orders/${order.id}/accept`, {
        committed_delivery_date: acceptForm.committed_delivery_date,
        checklist: {
          design_freeze: acceptForm.design_freeze,
          advance_received: acceptForm.advance_received,
          advance_amount: acceptForm.advance_amount ? parseFloat(acceptForm.advance_amount) : null,
        },
      });
      toast({ title: 'Order accepted' });
      setAcceptDialogOpen(false);
      onOrderUpdate?.();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Failed to accept', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleRequestFeasibility = async () => {
    setFeasibilityLoading(true);
    try {
      const mats = (order?.materials_involved && order.materials_involved.length > 0)
        ? order.materials_involved
        : ['diamond', 'stone', 'gold'];
      await api.post(`/orders/${order.id}/request-feasibility`, { departments: mats });
      toast({ title: 'Departments notified' });
      onOrderUpdate?.();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setFeasibilityLoading(false); }
  };

  const handleFeasibilityResponse = async (dept, status) => {
    setFeasibilityLoading(true);
    try {
      await api.post(`/orders/${order.id}/feasibility-response`, {
        department: dept, status, arrange_by: status === 'arrange' ? feasibilityArrangeBy : null,
      });
      toast({ title: `${dept} feasibility: ${status}` });
      setFeasibilityResponseDept('');
      setFeasibilityResponseStatus('');
      setFeasibilityArrangeBy('');
      onOrderUpdate?.();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setFeasibilityLoading(false); }
  };

  const feasibility = order?.material_feasibility;
  const feasibilityResponses = feasibility?.responses || {};
  const hasNotAvailable = Object.values(feasibilityResponses).some(r => r?.status === 'not_available');
  const DEPT_LABELS = { diamond: 'Diamond', stone: 'Colour Stone', gold: 'Gold' };
  const pendingDepts = Object.entries(feasibilityResponses)
    .filter(([, r]) => r?.status === 'pending')
    .map(([d]) => DEPT_LABELS[d] || d);
  // Accept gate is clear when there is nothing pending and nothing marked unavailable.
  const acceptGateClear = pendingDepts.length === 0 && !hasNotAvailable;
  const canRespondFeasibility = (dept) => {
    const map = { diamond: 'diamond_dept', stone: 'stone_dept', gold: 'inventory_dept' };
    return role === 'admin' || role === map[dept];
  };

  const handleEscalate = async (dept) => {
    setFeasibilityLoading(true);
    try {
      await api.post(`/orders/${order.id}/escalate-material`, { department: dept });
      toast({ title: `Escalation raised for ${dept} — inventory re-notified, admin flagged` });
      onOrderUpdate?.();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setFeasibilityLoading(false); }
  };

  // Completion verification handlers
  const handleCompletionImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCompletion(true);
    try {
      const result = await fileService.uploadFile(file);
      setCompletionImage(result.url);
    } catch (err) {
      toast({ title: 'Upload failed', variant: 'destructive' });
    } finally { setUploadingCompletion(false); }
  };

  const handleSubmitCompletion = async () => {
    if (!completionImage) {
      toast({ title: 'Please upload finished product image', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await api.post(`/orders/${order.id}/completion-upload`, {
        finished_image: completionImage,
        final_gross_weight: completionGrossWeight ? parseFloat(completionGrossWeight) : null,
        final_net_weight: completionNetWeight ? parseFloat(completionNetWeight) : null,
        final_stone_count: completionStoneCount ? parseInt(completionStoneCount) : null,
        purity_verified: completionPurity || null,
        notes: completionNotes,
      });
      toast({ title: 'Completion details uploaded' });
      setCompletionDialogOpen(false);
      setCompletionImage(''); setCompletionGrossWeight(''); setCompletionNetWeight('');
      setCompletionStoneCount(''); setCompletionPurity(''); setCompletionNotes('');
      onOrderUpdate?.();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleVerifyCompletion = async (action) => {
    setLoading(true);
    try {
      await api.post(`/orders/${order.id}/completion-verify`, {
        action,
        notes: action === 'reject' ? rejectNotes : '',
      });
      toast({ title: action === 'confirm' ? 'Completion confirmed' : 'Completion rejected' });
      setRejectNotes('');
      onOrderUpdate?.();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const verification = order?.completion_verification;


  return (
    <div className="bg-white border-b border-gray-200 p-2 md:p-3">
      {/* Stage Badge & Info */}
      <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-center justify-between'} mb-2 md:mb-3`}>
        <div className="flex items-center space-x-2 md:space-x-3 flex-wrap">
          <span className={`px-2 md:px-3 py-1 rounded-full text-white text-xs md:text-sm font-medium ${currentStageConfig.color}`}>
            {currentStageConfig.icon} {currentStageConfig.label}
          </span>
          {order?.assigned_worker_name && (
            <span className="text-xs md:text-sm text-gray-600">
              👷 {order.assigned_worker_name}
            </span>
          )}
          {order?.rework_count > 0 && (
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">
              ↻ {order.rework_count}
            </span>
          )}
          {(() => {
            const flag = getDeadlineFlag(order);
            if (flag.level === 'gray') return null;
            return (
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${DEADLINE_STYLES[flag.level]}`}>
                {flag.label}
              </span>
            );
          })()}
        </div>

        {/* Material Status */}
        <div className="flex items-center space-x-1 md:space-x-2 text-xs">
          {order?.gold_issued && <span className="px-1.5 md:px-2 py-0.5 md:py-1 bg-yellow-100 text-yellow-700 rounded">🥇</span>}
          {order?.diamond_issued && <span className="px-1.5 md:px-2 py-0.5 md:py-1 bg-blue-100 text-blue-700 rounded">💎</span>}
          {order?.stone_issued && <span className="px-1.5 md:px-2 py-0.5 md:py-1 bg-purple-100 text-purple-700 rounded">💠</span>}
          {verification?.status === 'pending' && <span className="px-1.5 md:px-2 py-0.5 md:py-1 bg-amber-100 text-amber-700 rounded text-[10px] font-bold">Verify Pending</span>}
          {verification?.status === 'confirmed' && <span className="px-1.5 md:px-2 py-0.5 md:py-1 bg-green-100 text-green-700 rounded text-[10px] font-bold">Verified</span>}
          {verification?.status === 'rejected' && <span className="px-1.5 md:px-2 py-0.5 md:py-1 bg-red-100 text-red-700 rounded text-[10px] font-bold">Rejected</span>}
        </div>
      </div>

      {/* Order Owner & Acceptance Status */}
      {order?.accepted ? (
        <div className="flex items-center gap-3 mb-2 text-xs flex-wrap">
          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded font-bold">Owner: {order.order_owner_name}</span>
          {order.committed_delivery_date && (
            <span className="text-gray-600">Committed: <span className="font-semibold">{order.committed_delivery_date}</span></span>
          )}
          {order.delivery_date && order.committed_delivery_date && order.delivery_date !== order.committed_delivery_date && (
            <span className="text-amber-600">(Sales promise: {order.delivery_date})</span>
          )}
        </div>
      ) : (
        <div className="mb-2 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-bold">Not Accepted</span>
            {/* Waiting on departments — Accept stays hidden until they clear */}
            {canAcceptOrder && pendingDepts.length > 0 && (
              <span className="flex items-center gap-1.5 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-bold" data-testid="waiting-dept-status">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                Waiting for {pendingDepts.join(', ')}
              </span>
            )}
            {/* Accept unlocks automatically once every needed dept has cleared */}
            {canAcceptOrder && acceptGateClear && (
              <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white h-7" onClick={() => {
                setAcceptForm(f => ({ ...f, committed_delivery_date: order.delivery_date || '' }));
                setAcceptDialogOpen(true);
              }} data-testid="accept-order-btn">
                Accept Order
              </Button>
            )}
            {/* Fallback: (re-)notify departments manually */}
            {canAcceptOrder && (
              <Button size="sm" variant="outline" className="text-xs h-7 border-blue-300 text-blue-600" onClick={handleRequestFeasibility} disabled={feasibilityLoading} data-testid="request-feasibility-btn">
                {feasibility ? 'Re-notify departments' : 'Notify departments'}
              </Button>
            )}
          </div>

          {/* Feasibility Responses */}
          {feasibility && (
            <div className="bg-gray-50 rounded-lg border p-2 space-y-1.5">
              <p className="text-[10px] text-gray-500 font-semibold uppercase">Material Feasibility</p>
              {Object.entries(feasibilityResponses).map(([dept, resp]) => {
                const labels = { diamond: 'Diamond', stone: 'Colour Stone', gold: 'Gold' };
                const isPending = resp?.status === 'pending';
                const canRespond = isPending && canRespondFeasibility(dept);
                return (
                  <div key={dept} className="flex items-center justify-between text-xs">
                    <span className="font-medium text-gray-700">{labels[dept] || dept}</span>
                    {isPending && !canRespond && <span className="text-amber-600 font-medium">Pending</span>}
                    {isPending && canRespond && (
                      <div className="flex gap-1">
                        <button onClick={() => handleFeasibilityResponse(dept, 'available')} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-bold hover:bg-green-200">Available</button>
                        <button onClick={() => { setFeasibilityResponseDept(dept); setFeasibilityResponseStatus('arrange'); }} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold hover:bg-amber-200">Arrange</button>
                        <button onClick={() => handleFeasibilityResponse(dept, 'not_available')} className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold hover:bg-red-200">N/A</button>
                      </div>
                    )}
                    {resp?.status === 'available' && <span className="text-green-600 font-bold">Available — {resp.responded_by_name}</span>}
                    {resp?.status === 'arrange' && <span className="text-amber-600 font-bold">Arrange by {resp.arrange_by} — {resp.responded_by_name}</span>}
                    {resp?.status === 'not_available' && (
                      <span className="flex items-center gap-1.5">
                        <span className="text-red-600 font-bold">Not Available — {resp.responded_by_name}</span>
                        {canAcceptOrder && (
                          <button onClick={() => handleEscalate(dept)} className="px-2 py-0.5 bg-red-500 text-white rounded text-[10px] font-bold hover:bg-red-600">Escalate</button>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
              {feasibilityResponseDept && feasibilityResponseStatus === 'arrange' && (
                <div className="flex items-center gap-2 mt-1 pl-2">
                  <Input type="date" value={feasibilityArrangeBy} onChange={e => setFeasibilityArrangeBy(e.target.value)} className="h-7 text-xs w-36" />
                  <Button size="sm" className="h-7 text-[10px] bg-amber-500 hover:bg-amber-600" onClick={() => handleFeasibilityResponse(feasibilityResponseDept, 'arrange')} disabled={!feasibilityArrangeBy}>Confirm</Button>
                  <button onClick={() => { setFeasibilityResponseDept(''); setFeasibilityResponseStatus(''); }} className="text-xs text-gray-400">Cancel</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Rework History */}
      {order?.rework_history?.length > 0 && (
        <div className="mb-2 bg-amber-50 border border-amber-200 rounded-lg p-2">
          <p className="text-[10px] font-bold text-amber-700 uppercase mb-1">Rework History ({order.rework_count})</p>
          <div className="space-y-1">
            {order.rework_history.slice().reverse().map((rw, i) => (
              <div key={i} className="text-xs flex items-start gap-2">
                <span className="text-amber-500 font-bold flex-shrink-0">#{order.rework_history.length - i}</span>
                <div>
                  <span className="font-medium text-gray-800">{rw.stage}</span>
                  <span className="text-gray-500"> — {rw.reason}</span>
                  <span className="text-gray-400 ml-1">({rw.rejected_by_name})</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-1 md:gap-2">

        {/* Job Card Assignment */}
        {(role === 'admin' || role === 'production_supervisor') && (
          <Dialog open={jobCardDialogOpen} onOpenChange={setJobCardDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className={`text-xs ${order?.job_card_number ? 'border-green-500 text-green-700' : 'border-blue-500 text-blue-700'}`}>
                <CircleDot className="w-3 h-3 mr-1" />
                {order?.job_card_number || 'Assign Job Card'}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{order?.job_card_number ? 'Update' : 'Assign'} Job Card Number</DialogTitle>
                <DialogDescription>
                  {order?.job_card_number
                    ? `Current: ${order.job_card_number}`
                    : 'Assign a unique job card/tag number for production tracking'}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <Label className="text-base">Job Card Number</Label>
                  <Input
                    value={jobCardNumber}
                    onChange={(e) => setJobCardNumber(e.target.value.toUpperCase())}
                    placeholder="Leave empty to auto-generate"
                    className="text-base py-6 font-mono font-bold"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format: JC-YYMMDD00001 (auto) or enter custom number
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>💡 Tip:</strong> Leave empty to auto-generate a unique job card number
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setJobCardDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleAssignJobCard} disabled={loading} className="bg-blue-500">
                  {order?.job_card_number ? 'Update' : 'Assign'} Job Card
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Worker Assignment */}
        {(canRecommendWorker || canAssignWorker) && (
          <Dialog open={workerDialogOpen} onOpenChange={setWorkerDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs">
                <Users className="w-3 h-3 mr-1" />
                {order?.assigned_worker_id ? 'Reassign' : 'Assign'} Worker
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {canAssignWorker ? 'Assign Worker' : 'Recommend Worker'}
                </DialogTitle>
                <DialogDescription>
                  {order?.recommended_worker_name && !order?.assigned_worker_id && (
                    <span className="text-blue-600">
                      Recommended: {order.recommended_worker_name}
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Select Worker</Label>
                  <Select value={selectedWorker} onValueChange={setSelectedWorker}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a worker" />
                    </SelectTrigger>
                    <SelectContent>
                      {workers.map((worker) => (
                        <SelectItem key={worker.id} value={worker.id}>
                          {worker.username}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Input
                    value={workerNotes}
                    onChange={(e) => setWorkerNotes(e.target.value)}
                    placeholder="Add notes..."
                  />
                </div>
              </div>
              <DialogFooter>
                {canRecommendWorker && !canAssignWorker && (
                  <Button onClick={handleRecommendWorker} disabled={loading || !selectedWorker}>
                    Recommend
                  </Button>
                )}
                {canAssignWorker && (
                  <Button onClick={handleAssignWorker} disabled={loading || !selectedWorker}>
                    Assign
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Stage Change */}
        {canChangeStage && (
          <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs">
                <ArrowRight className="w-3 h-3 mr-1" />
                Change Stage
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Change Production Stage</DialogTitle>
                <DialogDescription>
                  Current: {currentStageConfig.label}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>New Stage</Label>
                  <Select value={selectedStage} onValueChange={setSelectedStage}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {stages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          {STAGE_CONFIG[stage.id]?.icon} {stage.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Input
                    value={stageNotes}
                    onChange={(e) => setStageNotes(e.target.value)}
                    placeholder="Add notes..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleChangeStage} disabled={loading || !selectedStage}>
                  Change Stage
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* QC Approval */}
        {canQCApprove && order?.current_stage !== 'new' && order?.current_stage !== 'completed' && (
          <Dialog open={qcDialogOpen} onOpenChange={setQcDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs">
                <CheckCircle className="w-3 h-3 mr-1" />
                QC Review
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>QC Review</DialogTitle>
                <DialogDescription>
                  Stage: {currentStageConfig.label}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Notes (optional)</Label>
                  <Input
                    value={qcNotes}
                    onChange={(e) => setQcNotes(e.target.value)}
                    placeholder="Add QC notes..."
                  />
                </div>
              </div>
              <DialogFooter className="flex gap-2">
                <Button
                  variant="destructive"
                  onClick={() => handleQCApprove(false)}
                  disabled={loading}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleQCApprove(true)}
                  disabled={loading}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approve
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Completion Verification Upload (Inventory/Supervisor) */}
        {canUploadCompletion && order?.current_stage !== 'new' && order?.current_stage !== 'completed' && !verification && (
          <Dialog open={completionDialogOpen} onOpenChange={setCompletionDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs border-green-300 text-green-700">
                <CheckCircle className="w-3 h-3 mr-1" />
                Upload Completion
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Upload Completion Details</DialogTitle>
                <DialogDescription>Upload finished product image and weight details for verification.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Finished Product Image *</Label>
                  {completionImage ? (
                    <div className="relative">
                      <img src={completionImage} alt="Finished" className="w-full h-40 object-cover rounded-lg border" />
                      <button onClick={() => setCompletionImage('')} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-lg cursor-pointer hover:border-green-400 transition-colors">
                      <Upload className="w-6 h-6 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-500">{uploadingCompletion ? 'Uploading...' : 'Click to upload image'}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleCompletionImageUpload} />
                    </label>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Gross Weight (g)</Label>
                    <Input type="number" step="0.001" value={completionGrossWeight} onChange={e => setCompletionGrossWeight(e.target.value)} placeholder="grams" />
                  </div>
                  <div className="space-y-1">
                    <Label>Net Weight (g)</Label>
                    <Input type="number" step="0.001" value={completionNetWeight} onChange={e => setCompletionNetWeight(e.target.value)} placeholder="grams" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Stone Count</Label>
                    <Input type="number" value={completionStoneCount} onChange={e => setCompletionStoneCount(e.target.value)} placeholder="pcs" />
                  </div>
                  <div className="space-y-1">
                    <Label>Purity Verified</Label>
                    <Input value={completionPurity} onChange={e => setCompletionPurity(e.target.value)} placeholder="e.g., 22K" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Notes</Label>
                  <Input value={completionNotes} onChange={e => setCompletionNotes(e.target.value)} placeholder="Any observations" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCompletionDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSubmitCompletion} disabled={loading} className="bg-green-600 hover:bg-green-700">Upload</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Completion Verification Status & Actions */}
        {verification && verification.status === 'pending' && canVerifyCompletion && (
          <div className="flex items-center gap-1">
            <Button size="sm" className="text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => handleVerifyCompletion('confirm')} disabled={loading}>
              <CheckCircle className="w-3 h-3 mr-1" /> Confirm
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="text-xs border-red-300 text-red-600">
                  <XCircle className="w-3 h-3 mr-1" /> Reject
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm">
                <DialogHeader><DialogTitle>Reject Completion</DialogTitle></DialogHeader>
                <div className="space-y-2">
                  <Label>Reason for rejection</Label>
                  <Input value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} placeholder="What needs to be corrected?" />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRejectNotes('')}>Cancel</Button>
                  <Button className="bg-red-600 hover:bg-red-700" onClick={() => handleVerifyCompletion('reject')} disabled={loading}>Reject</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Material Request */}
        {canRequestMaterial && (
          <Dialog open={materialDialogOpen} onOpenChange={setMaterialDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs">
                <Package className="w-3 h-3 mr-1" />
                Request Material
              </Button>
            </DialogTrigger>


        {/* Assign Broker - For orders with solitaires */}
        {canAssignBroker && order?.solitaires && order.solitaires.length > 0 && (
          <Dialog open={brokerDialogOpen} onOpenChange={setBrokerDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs">
                <Diamond className="w-3 h-3 mr-1" />
                {order?.assigned_broker_name || 'Assign Broker'}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign Broker for Solitaire Procurement</DialogTitle>
                <DialogDescription>Select a broker to handle solitaire sourcing</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Select Broker</Label>
                  <Select value={selectedBroker} onValueChange={setSelectedBroker}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Choose broker" />
                    </SelectTrigger>
                    <SelectContent>
                      {brokers.map((broker) => (
                        <SelectItem key={broker.id} value={broker.id}>
                          {broker.username}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes</Label>
                  <textarea
                    value={brokerNotes}
                    onChange={(e) => setBrokerNotes(e.target.value)}
                    rows={3}
                    className="w-full mt-2 px-3 py-2 border rounded-md text-sm"
                    placeholder="Special instructions for broker..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBrokerDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleAssignBroker} disabled={loading || !selectedBroker}>
                  Assign Broker
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Material Request</DialogTitle>
                <DialogDescription>Request materials from inventory department</DialogDescription>
              </DialogHeader>

              <div className="space-y-5">
                {/* Material Type */}
                <div>
                  <Label className="text-base">Material Type *</Label>
                  <Select value={materialType} onValueChange={setMaterialType}>
                    <SelectTrigger className="text-base py-6">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gold">🥇 Gold</SelectItem>
                      <SelectItem value="diamond">💎 Diamond</SelectItem>
                      <SelectItem value="stone">💠 Stone</SelectItem>
                      <SelectItem value="other">📦 Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Worker Name - NOW MANDATORY */}
                <div>
                  <Label className="text-base">Worker Name *</Label>
                  <Select value={requestWorker} onValueChange={setRequestWorker}>
                    <SelectTrigger className="text-base py-6">
                      <SelectValue placeholder="Select worker" />
                    </SelectTrigger>
                    <SelectContent>
                      {workers.map((worker) => (
                        <SelectItem key={worker.id} value={worker.id} className="text-base">
                          {worker.username} {worker.worker_type ? `(${worker.worker_type})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-red-500 mt-1">
                    * Required: Select which worker will receive these materials
                  </p>
                </div>

                {/* Diamond Priority Selector - only for diamond requests */}
                {materialType === 'diamond' && (
                  <div className="border-t pt-4">
                    <Label className="text-base">Priority Level *</Label>
                    <p className="text-xs text-gray-500 mb-2">Set priority for diamond department queue</p>
                    <div className="grid grid-cols-4 gap-2 mt-1">
                      {[
                        { value: 'critical', label: 'Critical', color: 'bg-red-500 border-red-500 text-white', ring: 'ring-red-300' },
                        { value: 'high', label: 'High', color: 'bg-orange-500 border-orange-500 text-white', ring: 'ring-orange-300' },
                        { value: 'normal', label: 'Normal', color: 'bg-yellow-500 border-yellow-500 text-white', ring: 'ring-yellow-300' },
                        { value: 'low', label: 'Low', color: 'bg-blue-500 border-blue-500 text-white', ring: 'ring-blue-300' },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setDiamondPriority(opt.value)}
                          className={`py-2.5 text-sm font-semibold rounded-lg border-2 transition-all ${
                            diamondPriority === opt.value
                              ? `${opt.color} ring-2 ${opt.ring}`
                              : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                          }`}
                          data-testid={`priority-btn-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gold specific fields */}
                {materialType === 'gold' && (
                  <div className="space-y-4 border-t pt-4">
                    <div>
                      <Label className="text-base">Metal Purity *</Label>
                      <div className="grid grid-cols-5 gap-2 mt-2">
                        {['9K', '14K', '18K', '22K', '24K'].map((purity) => (
                          <button
                            key={purity}
                            type="button"
                            onClick={() => setMetalPurity(purity)}
                            className={`py-3 text-base font-semibold rounded-lg border-2 ${
                              metalPurity === purity
                                ? 'bg-amber-500 text-white border-amber-500'
                                : 'bg-white border-gray-300'
                            }`}
                          >
                            {purity}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-base">Total Weight (grams) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={weightGrams}
                          onChange={(e) => setWeightGrams(e.target.value)}
                          placeholder="Enter weight"
                          className="text-base py-6"
                        />
                      </div>
                      <div>
                        <Label className="text-base">Pure Gold Weight (grams)</Label>
                        <Input
                          value={
                            weightGrams && metalPurity
                              ? (parseFloat(weightGrams) * (parseInt(metalPurity) / 24)).toFixed(3)
                              : '0.000'
                          }
                          readOnly
                          className="bg-amber-50 text-amber-700 font-bold text-base py-6"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Formula: {weightGrams || '0'} × ({metalPurity?.replace('K', '') || '22'}/24)
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Diamond/Stone specific fields */}
                {(materialType === 'diamond' || materialType === 'stone') && (
                  <div className="grid grid-cols-2 gap-4 border-t pt-4">
                    <div>
                      <Label className="text-base">Weight (carats)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        value={weightCarats}
                        onChange={(e) => setWeightCarats(e.target.value)}
                        placeholder="Optional"
                        className="text-base py-6"
                      />
                    </div>
                    <div>
                      <Label className="text-base">Count (pieces)</Label>
                      <Input
                        type="number"
                        inputMode="numeric"
                        value={materialCount}
                        onChange={(e) => setMaterialCount(e.target.value)}
                        placeholder="Optional"
                        className="text-base py-6"
                      />
                    </div>
                  </div>
                )}

                {/* Other material fields - ENHANCED */}
                {materialType === 'other' && (
                  <div className="space-y-4 border-t pt-4">
                    <div>
                      <Label className="text-base">Material Category *</Label>
                      <Select value={otherMaterialCategory} onValueChange={setOtherMaterialCategory}>
                        <SelectTrigger className="text-base py-6">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gold_ornaments" className="text-base">Gold ornaments</SelectItem>
                          <SelectItem value="silver_ornaments" className="text-base">Silver ornaments</SelectItem>
                          <SelectItem value="platinum_ornaments" className="text-base">Platinum ornaments</SelectItem>
                          <SelectItem value="non_precious_ornaments" className="text-base">Non precious ornaments</SelectItem>
                          <SelectItem value="others" className="text-base">Others</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Show material name input if "Others" selected */}
                    {otherMaterialCategory === 'others' && (
                      <div>
                        <Label className="text-base">Material Name *</Label>
                        <Input
                          value={otherMaterialName}
                          onChange={(e) => setOtherMaterialName(e.target.value)}
                          placeholder="e.g., Rhodium, Polish, etc."
                          className="text-base py-6"
                        />
                      </div>
                    )}

                    <div>
                      <Label className="text-base">Purity</Label>
                      <Input
                        value={otherMaterialPurity}
                        onChange={(e) => setOtherMaterialPurity(e.target.value)}
                        placeholder="e.g., 22K, 92.5%, 950"
                        className="text-base py-6"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-base">Quantity (pcs)</Label>
                        <Input
                          type="number"
                          inputMode="numeric"
                          value={quantityPieces}
                          onChange={(e) => setQuantityPieces(e.target.value)}
                          placeholder="Number of pieces"
                          className="text-base py-6"
                        />
                      </div>
                      <div>
                        <Label className="text-base">Quantity (wt)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={quantityWeight}
                          onChange={(e) => setQuantityWeight(e.target.value)}
                          placeholder="Weight in grams"
                          className="text-base py-6"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Assign to Inventory Assistant */}
                <div className="border-t pt-4">
                  <Label className="text-base">Assign to Inventory Assistant *</Label>
                  <Select value={assignedInventory} onValueChange={setAssignedInventory}>
                    <SelectTrigger className="text-base py-6">
                      <SelectValue placeholder="Select inventory assistant" />
                    </SelectTrigger>
                    <SelectContent>
                      {inventoryUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id} className="text-base">
                          {user.username} {user.salesman_id ? `(${user.salesman_id})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Upload Image */}
                <div>
                  <Label className="text-base">Upload Image (optional)</Label>
                  <div className="mt-2">
                    {materialImageUrl ? (
                      <div className="relative">
                        <img src={materialImageUrl} alt="Material requirement" className="w-full h-48 object-cover rounded-lg" />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setMaterialImage(null);
                            setMaterialImageUrl('');
                          }}
                          className="absolute top-2 right-2 bg-white"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div>
                        {/* Camera input — opens camera directly on Android */}
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          id="materialCameraInput"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setMaterialImage(file);
                              setUploadingImage(true);
                              try {
                                const result = await fileService.uploadFile(file);
                                setMaterialImageUrl(result.url);
                              } catch (error) {
                                alert('Failed to upload image');
                              } finally {
                                setUploadingImage(false);
                                e.target.value = '';
                              }
                            }
                          }}
                        />
                        <input
                          type="file"
                          accept="image/*"
                          id="materialImageInput"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setMaterialImage(file);
                              setUploadingImage(true);
                              try {
                                const result = await fileService.uploadFile(file);
                                setMaterialImageUrl(result.url);
                              } catch (error) {
                                alert('Failed to upload image');
                              } finally {
                                setUploadingImage(false);
                                e.target.value = '';
                              }
                            }
                          }}
                        />
                        {uploadingImage ? (
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex items-center justify-center">
                            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                            <span className="ml-3">Uploading...</span>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex gap-3 justify-center">
                            <label htmlFor="materialCameraInput" className="cursor-pointer flex flex-col items-center gap-1 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 transition-colors">
                              <Camera className="w-5 h-5" />
                              <span className="text-xs">Camera</span>
                            </label>
                            <label htmlFor="materialImageInput" className="cursor-pointer flex flex-col items-center gap-1 px-4 py-2 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors">
                              <ImagesIcon className="w-5 h-5" />
                              <span className="text-xs">Gallery</span>
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Comments */}
                <div>
                  <Label className="text-base">Comments/Notes</Label>
                  <textarea
                    value={materialComments}
                    onChange={(e) => setMaterialComments(e.target.value)}
                    placeholder="Add any special instructions..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setMaterialDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleIssueMaterial} disabled={loading || !materialType || !assignedInventory} className="bg-emerald-500">
                  Create Request
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Material Requests - For All Users to View */}
        {(canRequestMaterial || canConfirmMaterial) && materialRequests.length > 0 && (
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`text-xs ${materialRequests.filter(r => r.status === 'pending' || r.status === 'partial').length > 0 ? 'bg-orange-50 border-orange-300 text-orange-700 font-medium' : ''}`}
              >
                <Package className="w-3 h-3 mr-1" />
                Material Requests ({materialRequests.filter(r => r.status === 'pending' || r.status === 'partial').length} Pending)
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl">Material Requests for This Order</DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {materialRequests.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No material requests for this order</p>
                  </div>
                ) : (
                  materialRequests.map((request) => (
                    <div key={request.id} className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-lg">Request #{request.request_number}</h3>
                          <p className="text-sm text-gray-600">From: {request.requested_by_name}</p>
                          <p className="text-sm text-blue-600">Assigned to: {request.assigned_to_name}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          request.status === 'issued'
                            ? 'bg-green-100 text-green-700'
                            : request.status === 'partial'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-orange-200 text-orange-800'
                        }`}>
                          {request.status === 'issued' ? '✅ Issued' : request.status === 'partial' ? '📦 Partial' : '⏳ Pending'}
                        </span>
                      </div>

                      {/* Progress Bar for Partial Issuances */}
                      {request.status === 'partial' && (
                        <div className="mb-3 p-2 bg-blue-50 rounded">
                          {(() => {
                            const requested = request.weight_grams || request.weight_carats || 0;
                            const totalIssued = request.total_issued || 0;
                            const unit = request.weight_grams ? 'g' : 'ct';
                            const percentage = requested > 0 ? (totalIssued / requested * 100).toFixed(0) : 0;

                            return (
                              <>
                                <p className="text-sm font-bold text-blue-700 mb-1">
                                  Issued: {totalIssued} / {requested} {unit} ({percentage}%)
                                </p>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div className="bg-blue-600 h-2 rounded-full" style={{width: `${percentage}%`}}></div>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      )}

                      {/* Material Details */}
                      <div className="space-y-2 mb-4">
                        <p className="text-base"><strong>Material:</strong> {request.material_type.toUpperCase()}</p>
                        {request.metal_purity && (
                          <>
                            <p className="text-base"><strong>Purity:</strong> {request.metal_purity}</p>
                            {request.weight_grams && (
                              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                                <p className="text-base"><strong>Total Weight:</strong> {request.weight_grams}g</p>
                                <p className="text-lg font-bold text-amber-700">
                                  <strong>Pure Gold:</strong> {(request.weight_grams * (parseInt(request.metal_purity) / 24)).toFixed(3)}g
                                </p>
                                <p className="text-xs text-gray-500">
                                  Formula: {request.weight_grams}g × ({request.metal_purity.replace('K', '')}/24)
                                </p>
                              </div>
                            )}
                          </>
                        )}
                        {!request.metal_purity && request.weight_grams && (
                          <p className="text-base"><strong>Weight:</strong> {request.weight_grams}g</p>
                        )}
                        {request.weight_carats && (
                          <p className="text-base"><strong>Weight:</strong> {request.weight_carats} carats</p>
                        )}
                        {request.count && (
                          <p className="text-base"><strong>Count:</strong> {request.count} pieces</p>
                        )}
                        {request.other_material_name && (
                          <p className="text-base"><strong>Material Name:</strong> {request.other_material_name}</p>
                        )}
                      </div>

                      {/* Image */}
                      {request.image_url && (
                        <div className="mb-4">
                          <img src={getFullImageUrl(request.image_url)} alt="Requirement" className="w-full rounded-lg max-h-64 object-cover" />
                        </div>
                      )}

                      {/* Comments */}
                      {request.comments && (
                        <div className="mb-4 p-3 bg-white rounded">
                          <p className="text-sm font-medium text-gray-700">Comments:</p>
                          <p className="text-sm text-gray-600">{request.comments}</p>
                        </div>
                      )}

                      {/* Confirm Button - For pending or partial requests assigned to current user */}
                      {(request.status === 'pending' || request.status === 'partial') && request.assigned_to === currentUser?.id && canConfirmMaterial && (
                        <Button
                          onClick={() => openConfirmIssuanceDialog(request)}
                          className="w-full bg-green-500 hover:bg-green-600 text-white"
                        >
                          {request.status === 'partial' ? '📦 Issue More Material' : '✅ Confirm Material Issued'}
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Confirm Material Issuance Dialog */}
        <Dialog open={confirmIssuanceDialogOpen} onOpenChange={setConfirmIssuanceDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Confirm Material Issuance</DialogTitle>
              <DialogDescription>
                Provide issuance details for Request #{selectedRequestForConfirm?.request_number}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5">
              {/* Request Details with Progress */}
              <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                <p className="text-sm"><strong>Material:</strong> {selectedRequestForConfirm?.material_type?.toUpperCase()}</p>

                {/* LOCKED WORKER DISPLAY */}
                {selectedRequestForConfirm?.worker_name && (
                  <div className="bg-teal-100 border-2 border-teal-400 p-3 rounded-lg mt-2">
                    <p className="text-sm font-bold text-teal-900 flex items-center">
                      🔒 Worker: {selectedRequestForConfirm.worker_name}
                    </p>
                    <p className="text-xs text-teal-700 mt-1">
                      Assigned by supervisor - locked
                    </p>
                  </div>
                )}

                {(() => {
                  const requested = selectedRequestForConfirm?.weight_grams || selectedRequestForConfirm?.weight_carats || 0;
                  const totalIssued = selectedRequestForConfirm?.total_issued || 0;
                  const remaining = requested - totalIssued;
                  const unit = selectedRequestForConfirm?.weight_grams ? 'grams' : 'carats';
                  const percentage = requested > 0 ? (totalIssued / requested * 100).toFixed(0) : 0;

                  return (
                    <>
                      <p className="text-sm"><strong>Requested:</strong> {requested} {unit}</p>
                      {totalIssued > 0 && (
                        <>
                          <p className="text-sm text-blue-600"><strong>Already Issued:</strong> {totalIssued} {unit}</p>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-600 h-2 rounded-full" style={{width: `${percentage}%`}}></div>
                          </div>
                          <p className="text-xs text-gray-500">{percentage}% completed</p>
                        </>
                      )}
                      <p className="text-sm font-bold text-green-600"><strong>Remaining:</strong> {remaining} {unit}</p>
                    </>
                  );
                })()}
              </div>

              {/* Issuance Type Radio */}
              <div className="border-t pt-4">
                <Label className="text-base mb-3 block">Issuance Type *</Label>
                <div className="flex gap-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="issuanceType"
                      value="full"
                      checked={issuanceType === 'full'}
                      onChange={(e) => setIssuanceType(e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-base">Full Issue</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="issuanceType"
                      value="partial"
                      checked={issuanceType === 'partial'}
                      onChange={(e) => setIssuanceType(e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-base">Partial Issue</span>
                  </label>
                </div>
              </div>

              {/* Weight Issuing Now */}
              <div>
                <Label className="text-base">
                  Weight Issuing Now *
                  {(() => {
                    const materialType = selectedRequestForConfirm?.material_type?.toLowerCase();
                    return (materialType === 'diamond' || materialType === 'stone' || materialType === 'colour_stone') && (
                      <span className="text-xs text-purple-600 ml-2">(Auto-calculated from entries)</span>
                    );
                  })()}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={weightIssuingNow}
                  onChange={(e) => setWeightIssuingNow(e.target.value)}
                  placeholder="Enter weight"
                  className={`text-base py-6 mt-2 ${
                    (() => {
                      const materialType = selectedRequestForConfirm?.material_type?.toLowerCase();
                      return (materialType === 'diamond' || materialType === 'stone' || materialType === 'colour_stone')
                        ? 'bg-purple-50 font-semibold text-purple-700'
                        : '';
                    })()
                  }`}
                  readOnly={(() => {
                    const materialType = selectedRequestForConfirm?.material_type?.toLowerCase();
                    return materialType === 'diamond' || materialType === 'stone' || materialType === 'colour_stone';
                  })()}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Max: {(() => {
                    const requested = selectedRequestForConfirm?.weight_grams || selectedRequestForConfirm?.weight_carats || 0;
                    const totalIssued = selectedRequestForConfirm?.total_issued || 0;
                    return (requested - totalIssued).toFixed(2);
                  })()} {selectedRequestForConfirm?.weight_grams ? 'grams' : 'carats'}
                </p>
              </div>

              {/* Previous Issuances */}
              {selectedRequestForConfirm?.partial_issuances?.length > 0 && (
                <div className="border-t pt-4">
                  <Label className="text-base mb-2 block">Previous Issuances:</Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {selectedRequestForConfirm.partial_issuances.map((issuance, idx) => (
                      <div key={idx} className="text-sm bg-gray-50 p-2 rounded">
                        <p><strong>{issuance.weight_issued}</strong> {selectedRequestForConfirm?.weight_grams ? 'g' : 'ct'} - {issuance.voucher_number}</p>
                        <p className="text-xs text-gray-600">
                          {new Date(issuance.issued_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} by {issuance.issued_by_name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Voucher Number */}
              <div className="border-t pt-4">
                <Label className="text-base">Voucher/Issue Number *</Label>
                <Input
                  value={voucherNumber}
                  onChange={(e) => setVoucherNumber(e.target.value)}
                  placeholder="e.g., VCH-2024-001"
                  className="text-base py-6 mt-2"
                />
              </div>

              {/* Diamond/Stone-Specific Fields - For Diamond AND Colour Stone Requests */}
              {(() => {
                const materialType = selectedRequestForConfirm?.material_type?.toLowerCase();
                const isGemstone = materialType === 'diamond' || materialType === 'stone' || materialType === 'colour_stone';
                const gemLabel = materialType === 'diamond' ? 'Diamond' : 'Colour Stone';

                return isGemstone && (
                  <div className="space-y-4 border-t pt-4 bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-base font-semibold text-purple-800 mb-2">💎 {gemLabel} Details</h3>

                  {diamondEntries.map((entry, index) => (
                    <div key={index} className="space-y-4 border-b pb-4 last:border-b-0 last:pb-0">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-purple-700">Entry {index + 1}</h4>
                        {index > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeDiamondEntry(index)}
                            className="text-red-500 hover:text-red-700 text-xs"
                          >
                            ❌ Remove
                          </Button>
                        )}
                      </div>

                      <div>
                        <Label className="text-base">{gemLabel} Category</Label>
                        <Input
                          value={entry.category}
                          onChange={(e) => updateDiamondEntry(index, 'category', e.target.value)}
                          placeholder={`e.g., ${materialType === 'diamond' ? 'VVS1 Round Brilliant' : 'Ruby, Emerald, Sapphire'}`}
                          className="text-base py-6 mt-2"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-base">Carats (Cts)</Label>
                          <Input
                            type="number"
                            step="0.001"
                            inputMode="decimal"
                            value={entry.cts}
                            onChange={(e) => updateDiamondEntry(index, 'cts', e.target.value)}
                            placeholder="0.000"
                            className="text-base py-6 mt-2"
                          />
                        </div>

                        <div>
                          <Label className="text-base">Grams (Auto)</Label>
                          <Input
                            value={entry.grams}
                            readOnly
                            className="bg-gray-100 text-gray-700 text-base py-6 mt-2"
                            placeholder="0.000"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-base">Number of Pieces</Label>
                        <Input
                          type="number"
                          inputMode="numeric"
                          value={entry.pieces}
                          onChange={(e) => updateDiamondEntry(index, 'pieces', e.target.value)}
                          placeholder="e.g., 5"
                          className="text-base py-6 mt-2"
                        />
                      </div>

                      <div>
                        <Label className="text-base">Description</Label>
                        <textarea
                          value={entry.description}
                          onChange={(e) => updateDiamondEntry(index, 'description', e.target.value)}
                          placeholder="Additional details..."
                          rows={2}
                          className="w-full text-base p-3 mt-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  ))}

                  {/* Add Button - Now at BOTTOM */}
                  <div className="border-t pt-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addDiamondEntry}
                      className="w-full text-base py-6"
                    >
                      ➕ Add Another {gemLabel} Category
                    </Button>
                  </div>

                  {/* Total Summary */}
                  {diamondEntries.length > 0 && (
                    <div className="bg-purple-100 p-3 rounded">
                      <p className="text-sm font-semibold text-purple-800">
                        📊 Total: {diamondEntries.reduce((sum, e) => sum + (parseFloat(e.cts) || 0), 0).toFixed(3)} carats
                        ({diamondEntries.reduce((sum, e) => sum + (parseFloat(e.grams) || 0), 0).toFixed(3)} grams),
                        {' '}{diamondEntries.reduce((sum, e) => sum + (parseInt(e.pieces) || 0), 0)} pieces
                      </p>
                    </div>
                  )}

                  <div className="flex items-center space-x-2 border-t pt-4">
                    <input
                      type="checkbox"
                      id="allRequirementsCleared"
                      checked={allRequirementsCleared}
                      onChange={(e) => setAllRequirementsCleared(e.target.checked)}
                      className="w-5 h-5 rounded"
                    />
                    <Label htmlFor="allRequirementsCleared" className="text-base cursor-pointer font-semibold text-purple-800">
                      ✅ All Requirements Cleared
                    </Label>
                  </div>
                  <p className="text-xs text-purple-600">
                    Check this box only when all {gemLabel.toLowerCase()} requirements for this order are fully satisfied.
                  </p>
                </div>
              );
            })()}

              {/* Needs Parcel Checkbox */}
              <div className="flex items-center space-x-2 border-t pt-4">
                <input
                  type="checkbox"
                  id="needsParcel"
                  checked={needsParcel}
                  onChange={(e) => setNeedsParcel(e.target.checked)}
                  className="w-5 h-5 rounded"
                />
                <Label htmlFor="needsParcel" className="text-base cursor-pointer">
                  Needs Parcel
                </Label>
              </div>

              {/* Parcel Details - Conditional */}
              {needsParcel && (
                <div className="space-y-4 border-t pt-4 bg-blue-50 p-4 rounded-lg">
                  <div>
                    <Label className="text-base">Actual Weight Issued *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={actualWeightIssued}
                      onChange={(e) => setActualWeightIssued(e.target.value)}
                      placeholder="Enter actual weight"
                      className="text-base py-6 mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedRequestForConfirm?.weight_grams ? 'in grams' : 'in carats'}
                    </p>
                  </div>

                  <div>
                    <Label className="text-base">Rate per {selectedRequestForConfirm?.weight_grams ? 'gram' : 'carat'} *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={parcelRate}
                      onChange={(e) => setParcelRate(e.target.value)}
                      placeholder="Enter rate"
                      className="text-base py-6 mt-2"
                    />
                  </div>

                  <div>
                    <Label className="text-base">Insurance Value</Label>
                    <Input
                      value={`₹ ${insuranceValue.toFixed(2)}`}
                      readOnly
                      className="bg-green-50 text-green-700 font-bold text-base py-6 mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Calculated: {actualWeightIssued || '0'} × {parcelRate || '0'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => setConfirmIssuanceDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmMaterialIssuance}
                disabled={loading || !voucherNumber.trim() || !weightIssuingNow || parseFloat(weightIssuingNow) <= 0 || (needsParcel && (!actualWeightIssued || !parcelRate))}
                className="bg-green-500 hover:bg-green-600"
              >
                {loading ? 'Confirming...' : 'Confirm & Issue'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Accept Order Dialog */}
      <Dialog open={acceptDialogOpen} onOpenChange={setAcceptDialogOpen}>
        <DialogContent className="max-w-md" data-testid="accept-order-dialog">
          <DialogHeader>
            <DialogTitle>Accept Order — {order?.order_number}</DialogTitle>
            <DialogDescription>Take ownership and commit delivery date. Production cannot start until accepted.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Delivery Dates */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <div className="flex justify-between"><span className="text-blue-600">Sales Promise:</span><span className="font-semibold">{order?.delivery_date || 'Not set'}</span></div>
            </div>
            <div className="space-y-1">
              <Label>Committed Delivery Date *</Label>
              <Input type="date" value={acceptForm.committed_delivery_date}
                onChange={e => setAcceptForm(f => ({...f, committed_delivery_date: e.target.value}))}
                data-testid="committed-date-input" />
              {order?.delivery_date && acceptForm.committed_delivery_date && acceptForm.committed_delivery_date > order.delivery_date && (
                <p className="text-xs text-amber-600 mt-1">Committed date is later than sales promise — variance will be tracked</p>
              )}
            </div>

            {/* Checklist */}
            <div className="space-y-3">
              <Label className="font-semibold">Pre-Acceptance Checklist</Label>
              {!['Misc Repair', 'Customer Repair'].includes(order?.order_type) && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={acceptForm.design_freeze}
                    onChange={e => setAcceptForm(f => ({...f, design_freeze: e.target.checked}))}
                    className="w-4 h-4 text-emerald-600 rounded" />
                  <span className="text-sm">Design freeze confirmed</span>
                </label>
              )}
              {/* Material feasibility — confirmed by inventory departments */}
              {feasibility && (
                <div className="bg-green-50 border border-green-200 rounded p-2 text-xs">
                  <p className="font-semibold text-green-700 mb-1">Material Feasibility Confirmed</p>
                  {Object.entries(feasibilityResponses).map(([dept, resp]) => {
                    const labels = { diamond: 'Diamond', stone: 'Colour Stone', gold: 'Gold' };
                    return (
                      <div key={dept} className="flex justify-between">
                        <span>{labels[dept]}</span>
                        <span className="font-medium">{resp?.status === 'available' ? 'Available' : resp?.status === 'arrange' ? `Arrange by ${resp.arrange_by}` : resp?.status}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {!['Stock Order', 'Misc Repair'].includes(order?.order_type) && (
                <>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={acceptForm.advance_received}
                      onChange={e => setAcceptForm(f => ({...f, advance_received: e.target.checked}))}
                      className="w-4 h-4 text-emerald-600 rounded" />
                    <span className="text-sm">Advance payment received</span>
                  </label>
                  {acceptForm.advance_received && (
                    <div className="ml-7 space-y-1">
                      <Label className="text-xs">Advance Amount</Label>
                      <Input type="number" value={acceptForm.advance_amount}
                        onChange={e => setAcceptForm(f => ({...f, advance_amount: e.target.value}))}
                        placeholder="Rs." className="h-8" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAcceptOrder} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-accept-btn">
              Accept & Own This Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductionPanel;
